import csrfHeaders from 'src/utils/csrfHeaders'

import { genericError } from './useRecord'

// Helpers shared between the PaymentElement and ExpressCheckoutElement
// submit paths. Both flows hit the same /payment_processors/stripe_intents
// endpoint and finalize the same way; what differs is where the contact
// information comes from (Contact step state vs. the wallet event payload)
// and the value of payment_method_used the controller uses to pick a
// payment_method_types tuple.

// Stripe doesn't localize errors for us server-side, so the two
// normalizers diverge:
//
// `normalizeStripeError` is for Stripe.js client-side errors
// (confirmPayment, confirmSetup, confirmCardPayment). error.message
// arrives localized because App.jsx passes `locale` to loadStripe, so
// we just surface it. v7 renders only `errors.base` in the alert (see
// StripePayment + ExpressCheckout); for PaymentElement flows Stripe
// Elements still renders card_error / validation_error inline on the
// field itself, which the caller handles by early-returning before
// reaching this normalizer.
//
// `normalizeServerError` is for the JSON our /payment_processors/
// stripe_intents endpoint renders. Three shapes come back:
//   - app-authored errors are tagged `type: 'custom'` by the controller
//     (UnsupportedAchAccountError, recaptcha-fail, the generic
//     donation_error fallback) — surface verbatim; they're intentional,
//     actionable guidance.
//   - Stripe card declines keep their specific message ("your card was
//     declined" — actionable, vs. a misleading "try again"). Server-
//     side, that message comes back in English, but card declines almost
//     always surface client-side first via Stripe.js (where Stripe.js
//     does localize). Server-side card-decline pathway is the marginal
//     case.
//   - everything else (Stripe technical errors — rate limit, API
//     connection, processing) → our localized generic, not an English
//     technical string.
const isCardError = error => error.type == 'card_error' || [
  'expired_card',
  'incorrect_cvc',
  'incorrect_number',
  'invalid_cvc',
  'invalid_expiry_month',
  'invalid_expiry_year',
  'invalid_number',
  'card_declined',
].includes(error.code)

export const normalizeStripeError = error =>
  ({ base: [ error.message ] })

export const normalizeServerError = (json, i18n) =>
  json.type == 'custom' || isCardError(json)
    ? { base: [ json.message ] }
    : genericError(i18n)

export const toStripeMinorUnits = (amount, currency) =>
  Math.round(Number(amount || 0) * (10 ** (currency.exponent || 0)))

// donation.state.subscription → Stripe billing-cycle params. one_time
// (and any unrecognized value) returns undefined, which the backend
// reads as "create a one-shot PaymentIntent." Recognized recurring
// frequencies route through SetupIntent + Stripe::Subscription.
export const subscriptionIntervals = {
  monthly:   { interval: 'month', interval_count: 1 },
  quarterly: { interval: 'month', interval_count: 3 },
  annual:    { interval: 'year',  interval_count: 1 },
}

export const createIntent = async ({
  stripeIntentsPath, orderId, locale, subscription, i18n,
}) => {
  try {
    const response = await fetch(stripeIntentsPath, {
      method: 'POST',
      headers: csrfHeaders(),
      body: JSON.stringify({ order_id: orderId, locale, subscription }),
    })
    const json = response.status == 204 ? {} : await response.json()
    return response.ok ? json : { errors: normalizeServerError(json, i18n) }
  } catch (error) {
    console.error('Error creating payment intent:', error)
    return { errors: genericError(i18n) }
  }
}

// PATCH the order with the resolved payment_method_id. For one-time
// PaymentIntent flows the Stripe charge has already succeeded by the
// time we get here, so failures are non-fatal for the donor (webhooks
// can recover the order ↔ payment_method link). For SetupIntent /
// subscription flows the backend creates the Stripe::Subscription as
// part of this PATCH and the first invoice can need 3DS — when that
// happens the response body carries a client_secret the caller has
// to confirm on-session.
//
// Returns:
//   { requiresAction: { client_secret } } — subscription 3DS challenge
//   { errors }                            — non-recoverable PATCH failure
//   {}                                    — finalized cleanly
export const finalizeIntent = async ({
  stripeIntentsPath, token, paymentMethodId, locale, intentType, i18n,
}) => {
  try {
    const response = await fetch([ stripeIntentsPath, token ].join('/'), {
      method: 'PATCH',
      headers: csrfHeaders(),
      body: JSON.stringify({ locale, payment_method_id: paymentMethodId }),
    })

    if (response.status == 204) return {}

    const json = await response.json()

    if (!response.ok) {
      // PaymentIntent path: charge already settled, log and move on.
      if (intentType == 'payment_intent') {
        console.error('Error finalizing payment intent:', json)
        return {}
      }
      // SetupIntent path: Subscription couldn't be created → fail.
      return { errors: normalizeServerError(json, i18n) }
    }

    if (json.client_secret) return { requiresAction: json }
    return {}
  } catch (error) {
    console.error('Error finalizing payment intent:', error)
    if (intentType == 'payment_intent') return {}
    return { errors: genericError(i18n) }
  }
}
